import React, { useState } from 'react';
import axios from 'axios';
import { Lock, Eye, EyeOff, X, Check, AlertCircle } from 'lucide-react';

import { useLanguage } from '../contexts/LanguageContext';
import { API } from '../lib/axiosClient';

const PasswordChangeDialog = ({ isOpen, onClose }) => {
  const { t } = useLanguage();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Passwort-Richtlinie prüfen — synchron mit Backend-Whitelist (auth_types.validate_password_policy)
  const SPECIAL_CHARS_REGEX = /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/;
  const checks = {
    length: newPassword.length >= 8,
    uppercase: /[A-Z]/.test(newPassword),
    lowercase: /[a-z]/.test(newPassword),
    digit: /\d/.test(newPassword),
    special: SPECIAL_CHARS_REGEX.test(newPassword),
    match: newPassword === confirmPassword && confirmPassword.length > 0,
  };
  const allValid = Object.values(checks).every(Boolean) && currentPassword.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allValid) return;
    setLoading(true);
    setError('');

    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setSuccess(true);
      setTimeout(() => {
        onClose();
        resetForm();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || t('password.changeError'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowCurrent(false);
    setShowNew(false);
    setError('');
    setSuccess(false);
  };

  const handleClose = () => {
    onClose();
    resetForm();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="password-change-dialog">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />

      {/* Dialog */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden border border-slate-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
              <Lock size={18} className="text-blue-600" />
            </div>
            <h2 className="text-base font-semibold text-slate-900">{t('password.changePassword')}</h2>
          </div>
          <button onClick={handleClose} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors" data-testid="password-dialog-close">
            <X size={18} className="text-slate-500" />
          </button>
        </div>

        {/* Success-Anzeige */}
        {success ? (
          <div className="px-6 py-10 flex flex-col items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
              <Check size={28} className="text-green-600" />
            </div>
            <p className="text-base font-medium text-green-700" data-testid="password-change-success">Passwort erfolgreich geändert</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
            {/* Fehler */}
            {error && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200" data-testid="password-change-error">
                <AlertCircle size={16} className="text-red-500 flex-shrink-0" />
                <span className="text-sm text-red-700">{error}</span>
              </div>
            )}

            {/* Aktuelles Passwort */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('password.currentPassword')}</label>
              <div className="relative">
                <input
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                  placeholder={t("password.currentPwdPlaceholder")}
                  autoComplete="current-password"
                  data-testid="current-password-input"
                />
                <button type="button" onClick={() => setShowCurrent(!showCurrent)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600">
                  {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Neues Passwort */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('password.newPassword')}</label>
              <div className="relative">
                <input
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                  placeholder={t("password.newPwdPlaceholder")}
                  autoComplete="new-password"
                  data-testid="new-password-input"
                />
                <button type="button" onClick={() => setShowNew(!showNew)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600">
                  {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Passwort bestätigen */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Neues Passwort bestätigen</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t("password.repeatPwdPlaceholder")}
                autoComplete="new-password"
                data-testid="confirm-password-input"
              />
            </div>

            {/* Richtlinien-Checks */}
            <div className="space-y-1.5 pt-1" data-testid="password-policy-checks">
              {[
                { key: 'length', label: 'Mindestens 8 Zeichen' },
                { key: 'uppercase', label: 'Großbuchstabe (A-Z)' },
                { key: 'lowercase', label: 'Kleinbuchstabe (a-z)' },
                { key: 'digit', label: 'Ziffer (0-9)' },
                { key: 'special', label: 'Sonderzeichen (!@#$%^&*()_+-=[]{}|;:,.<>?)' },
                { key: 'match', label: 'Passwörter stimmen überein' },
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center ${checks[key] ? 'bg-green-500' : 'bg-slate-300'}`}>
                    {checks[key] && <Check size={10} className="text-white" />}
                  </div>
                  <span className={`text-xs ${checks[key] ? 'text-green-700' : 'text-slate-500'}`}>{label}</span>
                </div>
              ))}
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={handleClose}
                className="flex-1 px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
                data-testid="password-cancel-btn">
                Abbrechen
              </button>
              <button type="submit" disabled={!allValid || loading}
                className="flex-1 px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="password-submit-btn">
                {loading ? t('password.savingBtn') : t('password.changeBtn')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default PasswordChangeDialog;
